import { ShoppingCartOutlined } from "@ant-design/icons";
import { Button, Flex, Input, Skeleton } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import {
  useGetProductCategoriesForOnlineOrder,
  useGetProductsForOnlineOrder,
  useGetStoreDetailsForOnlineOrder,
} from "../../api/api.get";
import EmptyComponent from "../../components/empty.component";
import { FeaturesItems } from "../../components/featured-items";
import { OrgDataComponent } from "../../components/org.data.component";
import ProductViewBottomSheet from "../../components/product.view.botomsheet";
import { RowItem } from "../../components/row.item";
import { useCartContext } from "../../providers/cart.provider";
import { useStoreContext } from "../../providers/store.provider";
import { IProductOnline } from "../../types/types";
import { capitalizeWithTrunc } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";

export default function OnlineDeliveryPage() {
  const { id } = useParams();
  const [openBottomSheet, setOpenBottomSheet] = useState<IProductOnline>();

  const { data: categoriesData } = useGetProductCategoriesForOnlineOrder(id);

  const { data: storeDetailsData, error } =
    useGetStoreDetailsForOnlineOrder(id);
  const [searchText, setSearchText] = useState<string>("");

  const [debouncedProductNameSearchText] = useDebounce(searchText, 300);

  const { data: productsByNameData, isLoading: isSearchLoading } =
    useGetProductsForOnlineOrder(id, debouncedProductNameSearchText);

  const [items, setItems] = useState<IProductOnline[]>([]);

  const navigate = useNavigate();
  const { currency } = useStoreContext();
  const {
    items: productsToCheckout,
    itemTotal,
    addItem,
    removeItem,
  } = useCartContext();

  useEffect(() => {
    if (productsByNameData) {
      setItems(productsByNameData);
    } else {
    }
  }, [productsByNameData]);

  const handleGoToCheckout = () => navigate(`/stores/${id}/online/checkout`);

  useEffect(() => {
    const _error: any = error;
    if (_error) {
      if (_error.status === 404) {
        navigate("/not-found");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  const [showTextOnly, setShowTextOnly] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowTextOnly(window.scrollY > 300); // You can adjust this threshold
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSelectCategory = (categoryName: string) => {
    if (searchText === categoryName) {
      setSearchText("");
    } else {
      setSearchText(categoryName);
    }
  };

  return (
    <div
      className="mx-auto max-w-5xl min-h-screen"
      style={{
        background: storeDetailsData?.bodyBackgroundColor ?? "white",
        color: storeDetailsData?.bodyFontColor ?? "red",
        // Clears the fixed cart bar so the last item stays reachable.
        paddingBottom: "calc(6rem + env(safe-area-inset-bottom))",
      }}
    >
      <ProductViewBottomSheet
        onAddItems={(product) => addItem(product)}
        onClose={() => setOpenBottomSheet(undefined)}
        onRemoveItems={(product) => removeItem(product)}
        open={!!openBottomSheet}
        productsToCheckout={productsToCheckout}
        product={openBottomSheet}
      />

      <div
        className="py-10 px-4"
        style={{
          background: storeDetailsData?.headerBackgroundColor ?? "white",
          color: storeDetailsData?.headerFontColor ?? "black",
        }}
      >
        <OrgDataComponent storeDetailsData={storeDetailsData} variant="bold" />
      </div>

      <div className="p-4">
        <div>
          {categoriesData && categoriesData.length > 0 && (
            <div
              className="my-0"
              style={{ position: "sticky", top: 0, zIndex: 999 }}
            >
              <div
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: showTextOnly ? 0 : 16,
                  padding: showTextOnly ? "0px" : "20px 0px",
                  backgroundColor: "",
                }}
              >
                {categoriesData?.map((cat, idx) => (
                  <div
                    key={idx}
                    className="item"
                    onClick={() => handleSelectCategory(cat.name)}
                    style={{
                      height: showTextOnly ? 40 : undefined,
                      padding: 2,
                      fontFamily: showTextOnly ? "sans-serif" : "inherit",
                      borderRadius: showTextOnly ? 0 : 10,
                      backgroundColor:
                        cat.name === searchText
                          ? "lightgrey"
                          : storeDetailsData?.bodyFontColor,
                      color: storeDetailsData?.bodyBackgroundColor ?? "inherit",
                      border: `1px solid ${storeDetailsData?.bodyBackgroundColor ?? "black"}`,
                    }}
                  >
                    {!showTextOnly && (
                      <img
                        src={cat.image ?? "/assets/images/no-picture.svg"}
                        alt="category"
                      />
                    )}

                    <div className="item-title">
                      {capitalizeWithTrunc(cat.name)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="my-2 flex flex-row gap-4">
            <Input.Search
              allowClear
              placeholder="Search for items..."
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              size="large"
            />

            <div className="flex flex-row gap-4 items-center">
              <strong id="total-amount">
                {currency}
                {toFixedDisplay(itemTotal)}
              </strong>
            </div>
          </div>

          {!searchText && (
            <FeaturesItems onClick={(item) => setOpenBottomSheet(item)} />
          )}

          <Flex gap={5} className="divSpread"></Flex>

          <div className="my-4 p-0" style={{}}>
            <Skeleton active loading={isSearchLoading}>
              {items.length === 0 && (
                <EmptyComponent
                  color={storeDetailsData?.bodyFontColor ?? "inherit"}
                  title="No Items Found. Please change search terms."
                />
              )}
              {items.length > 0 &&
                items.map((item, idx) => (
                  <RowItem
                    fontColor={storeDetailsData?.bodyFontColor}
                    item={item}
                    key={idx}
                    onAddItems={(product) => addItem(product)}
                    onClick={(product) => setOpenBottomSheet(product)}
                    onRemoveItems={(product) => removeItem(product)}
                    productsToCheckout={productsToCheckout}
                  />
                ))}
            </Skeleton>
          </div>
        </div>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 z-[1000] p-4"
        style={{
          boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.15)",
          paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
          background: storeDetailsData?.bodyBackgroundColor ?? "white",
        }}
      >
        <div className="mx-auto max-w-5xl">
          <Button
            type="primary"
            size="large"
            block
            aria-label="Go to checkout"
            onClick={handleGoToCheckout}
            disabled={productsToCheckout.length === 0}
            icon={<ShoppingCartOutlined />}
          >
            {currency}
            {toFixedDisplay(itemTotal)} · {productsToCheckout.length} items
          </Button>
        </div>
      </div>
    </div>
  );
}
